import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { LoginCredentials } from '../types/auth.types';
import { UserRole, RoleRoutes } from '@/types/global.types';
import { getLogoutRedirectUrl } from './useTenantBranding';

export const useAuth = () => {
  const navigate = useNavigate();
  const {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    error,
    viewAsRole,
    login: storeLogin,
    logout: storeLogout,
    clearError,
    setViewAsRole,
    getEffectiveRole,
    updateUserProfile,
    updateProfileSubmissionStatus,
    updateProfileCompleted,
  } = useAuthStore();

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      try {
        await storeLogin(credentials);
        const { user: loggedInUser } = useAuthStore.getState();

        if (loggedInUser) {
          // Redirect based on role
          const redirectPath = getRedirectPath(loggedInUser.role, loggedInUser.tenantId);
          navigate(redirectPath);
        }
      } catch (error) {
        // Error is already set in the store
        throw error;
      }
    },
    [storeLogin, navigate]
  );

  const logout = useCallback(() => {
    // Get tenant login URL before clearing auth state
    const currentUser = useAuthStore.getState().user;
    const redirectUrl = getLogoutRedirectUrl(currentUser);

    // Clear auth state
    storeLogout();

    // Redirect to tenant-specific login or default login
    if (redirectUrl.startsWith('http')) {
      // External URL (production subdomain)
      window.location.href = redirectUrl;
    } else {
      // Internal path (development)
      navigate(redirectUrl);
    }
  }, [storeLogout, navigate]);

  const getRedirectPath = (role: UserRole, tenantId: string | null): string => {
    const routeTemplate = RoleRoutes[role];

    if (role === UserRole.SUPER_ADMIN) {
      return '/admin/dashboard';
    }

    if (!tenantId) {
      return '/login';
    }

    // Replace :tenantId with actual tenant ID
    const basePath = routeTemplate.replace(':tenantId', tenantId);
    return `${basePath}/dashboard`;
  };

  const getDashboardPath = useCallback((): string => {
    if (!user) return '/login';
    return getRedirectPath(user.role, user.tenantId);
  }, [user]);

  const canViewAs = useCallback(
    (targetRole: UserRole): boolean => {
      if (!user) return false;
      if (user.role !== UserRole.DIRECTOR_GENERAL) return false;

      const allowedRoles: UserRole[] = [
        UserRole.PEDAGOGICAL_DIRECTOR,
        UserRole.FINANCIAL_MANAGER,
        UserRole.MARKETING_MANAGER,
        UserRole.COMMERCIAL_MANAGER,
        UserRole.BRANCH_MANAGER,
      ];

      return allowedRoles.includes(targetRole);
    },
    [user]
  );

  const isViewingAs = useCallback((): boolean => {
    return user?.role === UserRole.DIRECTOR_GENERAL && viewAsRole !== null;
  }, [user, viewAsRole]);

  const exitViewAs = useCallback(() => {
    setViewAsRole(null);
  }, [setViewAsRole]);

  return {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    error,
    viewAsRole,
    effectiveRole: getEffectiveRole(),
    login,
    logout,
    clearError,
    setViewAsRole,
    canViewAs,
    isViewingAs,
    exitViewAs,
    getDashboardPath,
    updateUserProfile,
    updateProfileSubmissionStatus,
    updateProfileCompleted,
  };
};

// Simple hook for components that only need to check auth status
export const useIsAuthenticated = (): boolean => {
  return useAuthStore((state) => state.isAuthenticated);
};

// Hook to get current user
export const useCurrentUser = () => {
  return useAuthStore((state) => state.user);
};
